import { useCallback, useEffect, useRef, useState } from "react";
import {
  computeLaunchReadiness,
  LaunchReadiness,
} from "../database/launch/launchReadiness";

// App-level event any save handler can dispatch to force the launch checklist
// to re-check (e.g. after connecting Stripe or adding quotes):
//   window.dispatchEvent(new Event(LAUNCH_READINESS_REFRESH));
export const LAUNCH_READINESS_REFRESH = "launch-readiness:refresh";

type State = {
  loading: boolean;
  readiness: LaunchReadiness | null;
};

// Reads the launch gates for a community and keeps them fresh: refetches on
// community change, window focus, and the LAUNCH_READINESS_REFRESH event.
// Refetches are debounced and never overlap so returning to a screen does not
// hammer Firestore or the status callables.
export function useLaunchReadiness(
  communityId: string | undefined,
  community: any
): State & { refresh: () => void } {
  const [state, setState] = useState<State>({
    loading: !!communityId,
    readiness: null,
  });

  // Latest community object without making it a fetch dependency (branding
  // edits should not trigger a refetch on every keystroke; focus/event do).
  const communityRef = useRef(community);
  communityRef.current = community;

  const inFlight = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runFetch = useCallback(async () => {
    if (!communityId || inFlight.current) return;
    inFlight.current = true;
    try {
      const readiness = await computeLaunchReadiness(
        communityId,
        communityRef.current
      );
      setState({ loading: false, readiness });
    } catch {
      setState((prev) => ({ loading: false, readiness: prev.readiness }));
    } finally {
      inFlight.current = false;
    }
  }, [communityId]);

  const refresh = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runFetch();
    }, 400);
  }, [runFetch]);

  // Initial load + reload when the community changes.
  useEffect(() => {
    if (!communityId) {
      setState({ loading: false, readiness: null });
      return;
    }
    setState((prev) => ({ ...prev, loading: !prev.readiness }));
    void runFetch();
  }, [communityId, runFetch]);

  // Refetch on focus and on the app-level refresh event.
  useEffect(() => {
    if (!communityId) return;
    const onFocus = () => refresh();
    const onRefresh = () => refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener(LAUNCH_READINESS_REFRESH, onRefresh);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(LAUNCH_READINESS_REFRESH, onRefresh);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [communityId, refresh]);

  return { ...state, refresh };
}
